import Input from '@jetbrains/ring-ui/components/input/input'
import Radio from '@jetbrains/ring-ui/components/radio/radio'
import React, {memo, useCallback, useEffect, useRef} from 'react'

import {useStoredSettingsValue} from '../../../../../../../../../contexts/StoredSettingsValue/StoredSettingsValueContext'
import useBooleanState from '../../../../../../../../../hooks/useBooleanState'
import CliErrorMessage from '../../SmartDetectors/CliErrorMessage/CliErrorMessage'
import {
  DOCKERFILE_PATH_PROPERTY_NAME,
  DOCKERFILE_SOURCE_PROPERTY_NAME,
} from '../JobStepRunInDocker.consts'
import {useStoredDockerData} from '../JobStepRunInDocker.hooks'
import type {JobStepRunInDockerProps} from '../JobStepRunInDocker.types'
import {getDockerValues} from '../JobStepRunInDocker.utils'

import {
  DOCKERFILE_PATH_PLACEHOLDER,
  DOCKERFILE_PATH_RADIO_NAME,
  DOCKERFILE_SOURCE_PLACEHOLDER,
  DOCKERFILE_SOURCE_RADIO_NAME,
} from './JobStepRunInDockerDockerfile.consts'
import {DockerfileMode, isDockerfileMode} from './JobStepRunInDockerDockerfile.types'

import styles from './JobStepRunInDockerDockerfile.css'

function JobStepRunInDockerDockerfile({
  step,
  stepIndex,
  onStepPropertyChange,
  onStepPropertiesChange,
}: JobStepRunInDockerProps) {
  const dockerfileSourceRef = useRef<HTMLTextAreaElement>(null)
  const dockerfilePathRef = useRef<HTMLInputElement>(null)

  const {setValueToStoredSettings} = useStoredSettingsValue()
  const {
    storedDockerfileSourceKey,
    storedDockerfilePathKey,
    storedDockerfilePathValue,
    storedDockerfileSourceValue,
  } = useStoredDockerData(step.type, stepIndex)
  const {dockerfileSourceValue, dockerfilePathValue} = getDockerValues(step)

  const [dockerfileSourceFocused, onFocusDockerfileSource, onBlurDockerfileSource] =
    useBooleanState()
  const [dockerfilePathFocused, onFocusDockerfilePath, onBlurDockerfilePath] = useBooleanState()

  useEffect(() => {
    const ref = dockerfilePathValue != null ? dockerfilePathRef : dockerfileSourceRef
    requestAnimationFrame(() => ref.current?.focus())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChangeDockerfileSource = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
      onStepPropertyChange(stepIndex, DOCKERFILE_SOURCE_PROPERTY_NAME, event.currentTarget.value)
    },
    [stepIndex, onStepPropertyChange],
  )

  const onChangeDockerfilePath = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
      onStepPropertyChange(stepIndex, DOCKERFILE_PATH_PROPERTY_NAME, event.currentTarget.value)
    },
    [stepIndex, onStepPropertyChange],
  )

  const onChangeDockerfileMode = useCallback(
    (mode: string) => {
      if (isDockerfileMode(mode)) {
        if (mode === DockerfileMode.PATH) {
          setValueToStoredSettings(
            storedDockerfileSourceKey,
            dockerfileSourceValue ?? storedDockerfileSourceValue,
          )
        } else {
          setValueToStoredSettings(
            storedDockerfilePathKey,
            dockerfilePathValue ?? storedDockerfilePathValue,
          )
        }
        onStepPropertiesChange(stepIndex, [
          {
            name: DOCKERFILE_SOURCE_PROPERTY_NAME,
            value: mode === DockerfileMode.PATH ? null : storedDockerfileSourceValue,
          },
          {
            name: DOCKERFILE_PATH_PROPERTY_NAME,
            value: mode === DockerfileMode.PATH ? storedDockerfilePathValue : null,
          },
        ])
      }
    },
    [
      dockerfilePathValue,
      dockerfileSourceValue,
      onStepPropertiesChange,
      setValueToStoredSettings,
      stepIndex,
      storedDockerfilePathKey,
      storedDockerfilePathValue,
      storedDockerfileSourceKey,
      storedDockerfileSourceValue,
    ],
  )

  return (
    <>
      <Radio
        value={dockerfilePathValue != null ? DockerfileMode.PATH : DockerfileMode.SOURCE}
        onChange={onChangeDockerfileMode}
      >
        <Radio.Item className={styles.radio} value={DockerfileMode.SOURCE}>
          {DOCKERFILE_SOURCE_RADIO_NAME}
        </Radio.Item>
        <Input
          className={styles.dockerfileSource}
          inputRef={dockerfileSourceRef}
          inputClassName={styles.dockerfileSourceInput}
          placeholder={DOCKERFILE_SOURCE_PLACEHOLDER}
          value={dockerfileSourceValue || storedDockerfileSourceValue || ''}
          disabled={dockerfileSourceValue == null}
          onFocus={onFocusDockerfileSource}
          onBlur={onBlurDockerfileSource}
          onChange={onChangeDockerfileSource}
          multiline
        />
        <Radio.Item className={styles.radio} value={DockerfileMode.PATH}>
          {DOCKERFILE_PATH_RADIO_NAME}
        </Radio.Item>
        <Input
          className={styles.dockerfilePath}
          inputRef={dockerfilePathRef}
          placeholder={DOCKERFILE_PATH_PLACEHOLDER}
          value={dockerfilePathValue || storedDockerfilePathValue || ''}
          disabled={dockerfilePathValue == null}
          onFocus={onFocusDockerfilePath}
          onBlur={onBlurDockerfilePath}
          onChange={onChangeDockerfilePath}
        />
      </Radio>
      <CliErrorMessage
        anchorElement={dockerfileSourceRef.current}
        name={DOCKERFILE_SOURCE_PROPERTY_NAME}
        value={dockerfileSourceValue ?? ''}
        disabled={dockerfileSourceValue == null}
        stepIndex={stepIndex}
        focused={dockerfileSourceFocused}
      />
      <CliErrorMessage
        anchorElement={dockerfilePathRef.current}
        name={DOCKERFILE_PATH_PROPERTY_NAME}
        value={dockerfilePathValue ?? ''}
        disabled={dockerfilePathValue == null}
        stepIndex={stepIndex}
        focused={dockerfilePathFocused}
      />
    </>
  )
}

export default memo<JobStepRunInDockerProps>(JobStepRunInDockerDockerfile)
